import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { Typography, Grid, Collapse, Button } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { FullSizeCenteredFlexBox } from '../../../../../Components/FlexBox';
import useAPI from '../../../../../api';
import FormTextField from '../../../../../Components/Form/FormTextField';
import FormRadioStack from '../../../../../Components/Form/FormRadioStack';
import FormSelect from '../../../../../Components/Form/FormSelect';
import FormAutoComplete from '../../../../../Components/Form/FormAutoComplete';
import { AGES, OCCUPATIONS } from '../../../Utils/constants';
import { getForcedMessages } from '../../../../../i18n';
import FormRating from '../../../../../Components/Form/FormRating';
import { useAtomValue } from 'jotai';
import { selectedBranchAtom } from '../../../../../Atoms';
import keysMapper from '../../../Utils/keysMapper';
import CampaignSelectionDialog from '../../../../CustomerPage/Shared/CampaignSelectionDialog';

const useStyles = () => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
  },
});

const CustomerPage = function CustomerPage({
  errors = null,
  practiceData = {},
  formData = {},
  edit = false,
  formDirty = false,
  handleSetCustomerData,
  customerData = null,
}) {
  const intl = useIntl();
  const selectedBranch = useAtomValue(selectedBranchAtom);
  const italianTerms = getForcedMessages('it');
  const classes = useStyles();
  const API = useAPI();
  const { watch, reset } = useFormContext();
  const watcher = watch();

  const [zipCodes, setZipCodes] = useState([]);
  const [campaignDialog, setCampaignDialog] = useState(null);

  const handleSearchCompanies = (companyValue, onSuccess, onFailure) => {
    //search companies if form is not touched in edit and different from backend data or if is touched and different from old inserted to prevent useless calls
    if (
      (!formDirty &&
        edit &&
        companyValue?.toUpperCase() !== practiceData?.company?.toUpperCase()) ||
      !edit ||
      (edit &&
        formDirty &&
        formData?.company?.toUpperCase() !== companyValue?.toUpperCase())
    )
      return API.searchCompanies(companyValue, onSuccess, onFailure);
    return Promise.resolve([]);
  };

  const handleSearchZipCodes = (zipCodeValue, onSuccess, onFailure) => {
    if (
      (!formDirty && edit && zipCodeValue !== practiceData?.zipCode) ||
      !edit ||
      (edit && formDirty && formData?.zipCode !== zipCodeValue)
    )
      return API.lookupZipCode(
        zipCodeValue,
        (res) => {
          setZipCodes(res);
          onSuccess(res?.map(({ zipCode }) => zipCode) || []);
        },
        onFailure
      );
    return Promise.resolve([]);
  };

  useEffect(() => {
    if (!watcher?.eligible || watcher?.eligible === 'No') {
      reset({
        ...formData,
        company1: '',
        company2: '',
        company3: '',
        rating1: null,
        rating2: null,
        rating3: null,
        eligible: 'No',
      });
    }
  }, [watcher?.eligible]);

  useEffect(() => {
    if (!watcher?.zipCode) return;

    const selectedZipCode = zipCodes?.find(
      ({ zipCode }) => zipCode === watcher.zipCode
    );
    if (selectedZipCode) {
      reset({
        ...formData,
        city: selectedZipCode?.city || '',
        province: selectedZipCode?.province || '',
      });
    }
  }, [watcher?.zipCode]);

  const handleSearchByFiscalCode = (fiscalCode) => {
    if (fiscalCode) {
      API.lookupCustomerByFiscalCode(
        selectedBranch.id,
        fiscalCode,
        (res) => {
          if (res?.activeCampaigns?.length > 1) {
            setCampaignDialog(res?.activeCampaigns);
            handleSetCustomerData({
              ...keysMapper(res),
              entityId: res?.contacts?.find(
                ({ contactTypeId }) => contactTypeId === 1
              )?.id,
            });
          } else {
            handleSetCustomerData({
              ...keysMapper(res),
              entityId: res?.contacts?.find(
                ({ contactTypeId }) => contactTypeId === 1
              )?.id,
              campaignId: res?.activeCampaigns?.[0]?.id,
            });
          }
        },
        (err) => {
          console.log(err);
        }
      );
    }
  };

  const handleSetCampaignId = (campaignId) => {
    handleSetCustomerData((prev) => ({ ...prev, campaignId }));
  };

  return (
    <>
      <CampaignSelectionDialog
        open={Boolean(campaignDialog)}
        activeCampaigns={campaignDialog}
        onClose={() => setCampaignDialog(null)}
        onSubmit={handleSetCampaignId}
      />
      <div style={classes.root}>
        <Grid container rowSpacing="18px" columnSpacing="16px">
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6">
              {intl.messages.INSERT_CUSTOMER_DATA}
            </Typography>
          </Grid>

          <Grid size={{ md: 6, sm: 6, xs: 12 }}>
            <FormTextField
              name="fiscalCode"
              error={errors?.fiscalCode}
              width="100%"
              required
              allUpperCase
              maxCharacters={16}
              validateCallback={(cf) =>
                cf.length === 16 ? true : intl.messages.CF_ERROR
              }
            />
          </Grid>
          <Grid size={{ md: 3, sm: 3, xs: 3 }}>
            {!practiceData?.id && !customerData?.parentId && (
              <FullSizeCenteredFlexBox>
                <Button
                  id="search-cf"
                  variant="outlined"
                  fullWidth
                  onClick={() => handleSearchByFiscalCode(watcher?.fiscalCode)}
                  sx={{ mb: '24px' }}
                >
                  {intl.messages.SEARCH}
                </Button>
              </FullSizeCenteredFlexBox>
            )}
          </Grid>
          <Grid size={{ md: 3, sm: 3, xs: 3 }} />

          <Grid size={{ md: 6, sm: 6, xs: 12 }}>
            <FormTextField
              name="customerName"
              allUpperCase
              required
              error={errors?.customerName}
            />
          </Grid>
          <Grid size={{ md: 6, sm: 6, xs: 12 }}>
            <FormTextField
              name="customerSurname"
              allUpperCase
              required
              error={errors?.customerSurname}
            />
          </Grid>
          <Grid size={{ md: 4, sm: 4, xs: 6 }}>
            <FormRadioStack
              name="gender"
              options={['M', 'F']}
              row
              required
              error={errors?.gender}
              defaultValue={formData?.gender}
            />
          </Grid>

          <Grid size={{ md: 4, sm: 4, xs: 6 }}>
            <FormSelect
              error={errors?.age}
              name="age"
              options={AGES}
              required
            />
          </Grid>

          <Grid size={{ md: 4, sm: 4, xs: 6 }}>
            <FormSelect
              name="citizen"
              error={errors?.citizen}
              options={['ITALIAN', 'UE', 'EXTRA_UE']}
              required
            />
          </Grid>

          <Grid size={{ md: 3.5, sm: 2, xs: 4 }}>
            <FormAutoComplete
              name="zipCode"
              required
              error={errors?.zipCode}
              validateCallback={(zCode) =>
                !isNaN(zCode) || intl.messages.NOLETTERS_VALIDATION
              }
              fetchCallback={handleSearchZipCodes}
              noCustomValue
            />
          </Grid>
          <Grid size={{ md: 6.5, sm: 8, xs: 6 }}>
            <FormTextField
              name="city"
              allUpperCase
              required
              error={errors?.city}
            />
          </Grid>

          <Grid size={{ md: 2, sm: 2, xs: 2 }}>
            <FormTextField
              allUpperCase
              name="province"
              error={errors?.province}
              required
              maxCharacters={2}
            />
          </Grid>

          <Grid size={{ md: 4, sm: 6, xs: 6 }}>
            <FormSelect
              error={errors?.occupation}
              name="occupation"
              options={OCCUPATIONS}
              required
            />
          </Grid>
          <Grid size={{ md: 5, sm: 6, xs: 6 }}>
            <FormAutoComplete
              name="company"
              allUpperCase
              required={watcher?.occupation !== italianTerms.STUDENT}
              error={errors?.company}
              fetchCallback={handleSearchCompanies}
            />
          </Grid>
          <Grid size={{ md: 3, sm: 6, xs: 6 }}>
            <FormRadioStack
              name="eligible"
              options={[intl.messages.YES, intl.messages.NO]}
              row
              required
              defaultValue={formData?.eligible}
              error={errors?.eligible}
            />
          </Grid>

          <Grid size={{ md: 9, sm: 6, xs: 6 }}>
            <Collapse
              in={watcher?.eligible === intl.messages.YES}
              unmountOnExit
            >
              <Typography variant="subtitle1" sx={{
                mb: "16px"
              }}>
                {intl.messages.SELECT_AND_RATE_COMPANIES}
              </Typography>
              {[...Array(3)].map((_, index) => {
                const companyKey = `company${index + 1}`;
                const ratingKey = `rating${index + 1}`;

                return (
                  <Grid
                    container
                    rowSpacing="18px"
                    columnSpacing="16px"
                    key={companyKey + ratingKey}
                  >
                    <Grid size={{ md: 8, sm: 6, xs: 6 }}>
                      <FormSelect
                        name={companyKey}
                        error={
                          typeof errors[companyKey] !== 'undefined'
                            ? errors[companyKey]
                            : null
                        }
                        noTranslation
                        options={[
                          'NET',
                          'CARDIFF',
                          intl.messages.OTHER?.toUpperCase(),
                        ]}
                      />
                    </Grid>
                    <Grid size={{ md: 4, sm: 6, xs: 6 }}>
                      <FormRating
                        name={ratingKey}
                        label={intl.messages.VALUE_1_5}
                        required={watcher[companyKey]}
                        error={
                          typeof errors[ratingKey] !== 'undefined'
                            ? errors[ratingKey]
                            : null
                        }
                        disabled={
                          !Object.hasOwn(watcher, companyKey) ||
                          !watcher[companyKey]
                        }
                      />
                    </Grid>
                  </Grid>
                );
              })}
            </Collapse>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

CustomerPage.propTypes = {
  errors: PropTypes.object,
  practiceData: PropTypes.object,
  formData: PropTypes.object,
  edit: PropTypes.bool,
  formDirty: PropTypes.bool,
};

export default CustomerPage;
